import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import { MediaTurbine, ProgrammaticTargeting, DeviceSimulator } from "@/components/site/VisualStorytelling";
import {
  Compass,
  Lightbulb,
  Brush,
  Rocket,
  BarChart3,
  TrendingUp,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Zap,
  Target,
  FileCheck,
} from "lucide-react";

export const Route = createFileRoute("/process")({
  head: () => ({
    meta: [
      {
        title: "Our Process & Growth System — KMWAdvertising · Media Strategy & Execution",
      },
      {
        name: "description",
        content:
          "Discover KMWAdvertising's 6-step growth framework and precision media workflow from formulation and audience diagnostics to full-scale multi-channel deployment.",
      },
      {
        property: "og:title",
        content: "Our Process & Growth System — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Discover our structured 6-step advertising workflow delivering maximum return on ad spend (ROAS) and compounding brand scale.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ProcessPage,
});

const sixSteps = [
  {
    step: "01",
    phase: "Diagnostics",
    title: "Discovery & Audience Diagnostics",
    desc: "We begin with rigorous demographic auditing, competitor footprint benchmarking, and customer browsing diagnostics. We analyze historical ad data to uncover audience intent before committing media capital.",
    icon: Compass,
    color: "var(--glow-orange)",
    badge: "Formulation Phase",
    deliverables: [
      "Audience demographic & psycho-graphic profiling",
      "Historical ad spend & CPA performance audit",
      "Competitor media mix & share-of-voice benchmark",
      "Channel feasibility & rate index modeling",
    ],
  },
  {
    step: "02",
    phase: "Architecture",
    title: "Integrated Strategy & Channel Mix",
    desc: "We architect custom media allocation blueprints matching your unit economics and growth velocity. We determine the exact budget weightings across Digital, TV, OOH, and Radio.",
    icon: Lightbulb,
    color: "var(--glow-purple)",
    badge: "Blueprint Phase",
    deliverables: [
      "Cross-channel budget allocation matrix",
      "Target CAC, ROAS, and volume KPI forecasting",
      "Campaign flight schedule & milestone calendar",
      "Creative brief strategy & dynamic angle matrix",
    ],
  },
  {
    step: "03",
    phase: "Production",
    title: "Creative Development & Asset Engine",
    desc: "Our creative studio and copy team produce high-converting ad assets, video commercials, interactive landing pages, and regional adaptations engineered to hook attention within 3 seconds.",
    icon: Brush,
    color: "var(--glow-cyan)",
    badge: "Creative Engineering",
    deliverables: [
      "High-converting video commercials & motion ads",
      "Multi-variant ad creative iterations & hooks",
      "High-speed, responsive landing page architectures",
      "Regional language & cultural localization",
    ],
  },
  {
    step: "04",
    phase: "Deployment",
    title: "Precision Media Launch & Go-To-Market",
    desc: "We execute campaign launches across programmatic DSP consoles, direct publisher placements, TV broadcast networks, and search platforms with server-side tracking and verified pixel firing.",
    icon: Rocket,
    color: "var(--glow-orange)",
    badge: "Flight Activation",
    deliverables: [
      "Programmatic DSP & direct ad network configuration",
      "Broadcast spot scheduling & prime-time verification",
      "Server-side Conversions API & GA4 attribution setup",
      "Real-time fraud filtering & brand safety locks",
    ],
  },
  {
    step: "05",
    phase: "Refinement",
    title: "Algorithmic Optimization & Bid Tuning",
    desc: "Daily data parsing and multivariate testing isolate top-performing audiences, keywords, and creative hooks. We reallocate budget hourly to drive down CPA and increase conversion rates.",
    icon: BarChart3,
    color: "var(--glow-purple)",
    badge: "Performance Loop",
    deliverables: [
      "Hourly programmatic bid and audience adjustments",
      "Creative fatigue rotation & multi-arm bandit testing",
      "Landing page conversion rate optimization (CRO)",
      "Multi-touch attribution & cross-device tracking",
    ],
  },
  {
    step: "06",
    phase: "Expansion",
    title: "Vertical Scaling & Multi-Market Compounding",
    desc: "Once unit economics are validated, we scale budget efficiently, expanding from localized flights to national networks and international territories across Asia, UAE, and Europe.",
    icon: TrendingUp,
    color: "var(--glow-cyan)",
    badge: "Scale Velocity",
    deliverables: [
      "Aggressive budget scaling on top ROAS channels",
      "Cross-border international media expansion",
      "Omnichannel flywheel compounding & retargeting",
      "Executive growth dashboard & live performance reviews",
    ],
  },
];

const pillars = [
  {
    title: "Zero Ad Waste Philosophy",
    desc: "Every dollar is tracked against verifiable business outcomes. We aggressively eliminate underperforming placements.",
    icon: ShieldCheck,
  },
  {
    title: "Direct Media Relationships",
    desc: "Direct negotiated inventory access with major TV networks, OTT platforms, billboard owners, and FM stations.",
    icon: Zap,
  },
  {
    title: "Full Attribution Transparency",
    desc: "No black-box reporting. You receive live dashboard access with granular breakdown of impressions, clicks, leads, and ROAS.",
    icon: FileCheck,
  },
  {
    title: "Dedicated Media Directors",
    desc: "Direct access to senior media planners with 10+ years experience rather than junior account coordinators.",
    icon: Target,
  },
];

export function ProcessPage() {
  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-16 md:py-24 border-b border-white/5">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)]" />
          <div className="glow-orb right-[-100px] top-30 h-[380px] w-[380px] bg-[var(--glow-orange)]" />

          <div className="container-x relative z-10">
            {/* Breadcrumbs */}
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-[var(--glow-orange)] font-semibold">Our Process</span>
            </div>

            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--glow-orange)] shadow-[0_0_8px_var(--glow-orange)] animate-pulse" />
                METHODOLOGY & WORKFLOW
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                The Science of <br />
                <span className="text-gradient">High-Impact Growth.</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg md:text-xl text-white/90 font-medium leading-relaxed">
                We replace marketing guesswork with a closed-loop engineering system. Our 6-step growth framework connects deep audience diagnostics with high-velocity multi-channel execution.
              </p>

              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                From initial market discovery and rate benchmarking to programmatic DSP launching and cross-border scaling, every stage is optimized for unit economics, transparency, and compounding return on ad spend.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/contact-us" className="btn-hero !py-3 !px-7">
                  Start Your Campaign <ArrowRight className="h-4 w-4 ml-1 inline" />
                </Link>
                <Link to="/services" className="btn-ghost !py-3 !px-6">
                  Explore Services <ArrowUpRight className="h-4 w-4 ml-1 inline" />
                </Link>
              </div>
            </div>

            {/* Metric Highlights */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 pt-8 border-t border-white/10">
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-[var(--glow-orange)]">6 Stages</div>
                <div className="text-xs text-muted-foreground mt-1">Closed-Loop Framework</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-white">100%</div>
                <div className="text-xs text-muted-foreground mt-1">Data-Backed Placements</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-[var(--glow-purple)]">Real-Time</div>
                <div className="text-xs text-muted-foreground mt-1">Live ROAS Attribution</div>
              </div>
              <div className="glass rounded-2xl p-4 border border-white/10">
                <div className="text-2xl sm:text-3xl font-display font-bold text-white">Zero</div>
                <div className="text-xs text-muted-foreground mt-1">Wasted Media Budget</div>
              </div>
            </div>
          </div>
        </section>

        {/* 6-STEP COMPREHENSIVE TIMELINE GRID */}
        <section className="py-16 md:py-24 border-b border-white/5">
          <div className="container-x">
            <div className="max-w-3xl mb-14">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-orange)] font-semibold mb-3">
                <Sparkles className="h-3.5 w-3.5" />
                OUR 6-STEP GROWTH SYSTEM
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                How We Engineer <span className="text-gradient">Predictable ROI.</span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground">
                Each stage of our process builds upon validated data, reducing campaign friction and ensuring marketing budgets scale profitably.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sixSteps.map((s, idx) => {
                const IconComponent = s.icon;
                return (
                  <div
                    key={s.step}
                    className="rounded-3xl p-7 bg-[#08080d] border border-white/10 flex flex-col justify-between relative overflow-hidden shadow-2xl hover:border-white/20 transition-all duration-300 group"
                  >
                    <div
                      className="absolute -top-16 -right-16 h-36 w-36 blur-3xl opacity-10 group-hover:opacity-25 transition-opacity pointer-events-none"
                      style={{ background: s.color }}
                    />

                    <div>
                      {/* Top Bar */}
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <div className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-white shrink-0 group-hover:scale-105 transition-transform">
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <span className="text-xs font-mono font-bold tracking-widest text-[var(--glow-orange)] bg-white/5 px-3 py-1 rounded-md border border-white/5">
                          STEP {s.step}
                        </span>
                      </div>

                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground mb-1">
                        {s.badge}
                      </div>

                      <h3 className="font-display text-xl sm:text-2xl font-bold text-white mb-3">
                        {s.title}
                      </h3>

                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                        {s.desc}
                      </p>

                      {/* Deliverables List */}
                      <div className="space-y-2 pt-4 border-t border-white/5">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-white/70 block mb-2">
                          Key Milestone Deliverables:
                        </span>
                        {s.deliverables.map((item, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-slate-300">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">
                        Stage {idx + 1} of 6
                      </span>
                      <div className="h-2 w-2 rounded-full bg-[var(--glow-orange)]" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* INTERACTIVE MEDIA TURBINE STORYTELLING */}
        <MediaTurbine />

        {/* PROGRAMMATIC TARGETING & SIMULATOR MODULES */}
        <ProgrammaticTargeting />
        <DeviceSimulator />

        {/* QUALITY PILLARS */}
        <section className="py-16 md:py-24 bg-black/60 border-t border-b border-white/5">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--glow-orange)] font-semibold mb-3">
                <ShieldCheck className="h-3.5 w-3.5" />
                EXECUTION EXCELLENCE
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
                Built on Rigor, <span className="text-gradient">Transparency & Integrity.</span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground">
                We operate as an extension of your growth team, providing uncompromised transparency at every phase of media deployment.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {pillars.map((p, idx) => {
                const IconComponent = p.icon;
                return (
                  <div
                    key={p.title}
                    className="p-6 rounded-3xl bg-[#08080d] border border-white/10 flex flex-col justify-between hover:border-white/20 transition-all duration-300 shadow-xl"
                  >
                    <div>
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-orange)] mb-4">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <h3 className="font-display text-lg font-bold text-white mb-2">{p.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CALL TO ACTION */}
        <section className="py-20 md:py-28 relative overflow-hidden bg-black">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />
          <div className="container-x relative z-10 text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[var(--glow-orange)] border border-white/10 mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              LET'S BUILD YOUR CAMPAIGN
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
              Put Our 6-Step Growth Engine to Work for Your Business.
            </h2>

            <p className="mt-5 text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our strategy team today to schedule an in-depth audit of your current ad spend, channel mix, and untapped growth opportunities.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact-us" className="btn-hero !py-3.5 !px-8 text-sm sm:text-base">
                Request Media Consultation <ArrowRight className="h-4 w-4 ml-1 inline" />
              </Link>
              <Link to="/work" className="btn-ghost !py-3.5 !px-7 text-sm sm:text-base">
                View Industry Work
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
